import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { CollapsibleSection } from "components/designV2/collapsibleSection/collapsibleSection";
import { Modal } from "components/designV2/modal/modal";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { Text } from "components/designV2/text/text";
import { TextArea } from "components/designV2/textarea/textarea";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import {
  ApplicationDto,
  ApplicationResolution,
  ApplicationStatus,
  ApplicationUserSkillDto,
  ApplicationWorkerEndorsementDto,
  ApplicationWorkerStat,
} from "model/Application";
import { GigType } from "model/Gig";
import {
  getWorkerImg,
  getWorkerVideo,
} from "services/firebase/firebase.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { fromApiPhoneNumber } from "utils/phoneNumber.logic";
import { useApplicationStore } from "web-apps/company/stores/applicationStore/applicationStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { formatWorkerDateOfBirth } from "web-apps/company/utils/utils";
import { toApplicationStatusUpdate } from "web-apps/company/components/applications/applicationStatus.logic";
import {
  shouldShowApplicationContract,
  toApplicationStatusChip,
} from "web-apps/company/components/applications/applicationList.display.logic";
import { GigContract } from "web-apps/company/components/gig/gigApplicants/gigContract.component";
import { WorkerDocuments } from "web-apps/company/components/workerDocument/workerDocument.component";
import {
  ApplicantContentGrid,
  ApplicantInfoGrid,
  ApplicantInfoItem,
  ApplicantPanelCard,
  ApplicantPanelHeading,
  ApplicantPanelSection,
  ApplicantPanelText,
  ApplicantProfileAvatar,
  ApplicantProfileColumn,
  ApplicantProfileImage,
  ApplicantProfileIdentity,
  ApplicantProfileName,
  ApplicantProfilePanel,
  ApplicantProfileTag,
  ApplicantProfileTags,
  ApplicantSection,
  ApplicantStatusBadge,
  ApplicantSummaryGrid,
  ApplicantSummaryItem,
  ApplicantVideo,
  ApplicantVideoWrapper,
  ContactLink,
  ContactList,
  EndorsementList,
  FooterActions,
  ProfileMeta,
  SkillCopy,
  SkillItem,
  SkillsList,
  StatCard,
  StatGrid,
  StatValue,
} from "./applicantDetailsModal.styled";

interface Props {
  readonly applicant: ApplicationDto;
  readonly onApplicationUpdated?: (application: ApplicationDto) => void;
  readonly setShowApplicationModal: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  readonly showApplicationModal: boolean;
  readonly setApplications?: React.Dispatch<
    React.SetStateAction<ApplicationDto[]>
  >;
  readonly unhandled?: boolean;
  readonly setWorkerApplication?: React.Dispatch<
    React.SetStateAction<ApplicationDto | null | undefined>
  >;
  readonly presentation?: "modal" | "panel";
}

interface ApplicantDetailsShellProps {
  readonly children: React.ReactNode;
  readonly footer: React.ReactNode;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly presentation: "modal" | "panel";
  readonly title: string;
}

const APPLICANT_DETAILS_PANEL_TITLE_ID = "applicant-details-panel-title";

const ApplicantDetailsShell: React.FC<ApplicantDetailsShellProps> = ({
  children,
  footer,
  isOpen,
  onClose,
  presentation,
  title,
}) =>
  presentation === "panel" ? (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      titleId={APPLICANT_DETAILS_PANEL_TITLE_ID}
      footer={footer}
    >
      {children}
    </SidePanel>
  ) : (
    <Modal
      label={title}
      size="medium"
      isOpen={isOpen}
      onClose={onClose}
      footer={footer}
    >
      {children}
    </Modal>
  );

interface WorkerStatLookup {
  readonly name: string;
  readonly stats: unknown;
}

type ApplicationResponse = "accept" | "reject";

const isWorkerStat = (value: unknown): value is ApplicationWorkerStat => {
  if (typeof value !== "object" || value === null) return false;
  return (
    "stat" in value &&
    "value" in value &&
    typeof value.stat === "string" &&
    typeof value.value === "number"
  );
};

const getWorkerStat = ({ stats, name }: WorkerStatLookup): number | null => {
  if (!Array.isArray(stats)) return null;
  return (
    stats.find(
      (stat): stat is ApplicationWorkerStat =>
        isWorkerStat(stat) && stat.stat === name,
    )?.value ?? null
  );
};

interface ExperienceLabelParams {
  readonly experience: number;
  readonly monthsLabel: string;
  readonly yearsLabel: string;
}

const toExperienceLabel = (params: ExperienceLabelParams): string => {
  if (params.experience === 0) return `3-9 ${params.monthsLabel}`;
  if (params.experience < 2) return `1-2 ${params.yearsLabel}`;
  if (params.experience < 5) return `2-5 ${params.yearsLabel}`;
  return `+5 ${params.yearsLabel}`;
};

const DESTRUCTIVE_BUTTON_STYLES = {
  backgroundColor: ColorV2.NegativeRed,
  borderColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.DeclinedRed,
  activeBackgroundColor: ColorV2.NegativeRed,
};

const ASSIGN_BUTTON_STYLES = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

export const ApplicantDetailsModal: React.FC<Props> = ({
  applicant,
  onApplicationUpdated,
  setShowApplicationModal,
  showApplicationModal,
  setApplications,
  unhandled,
  setWorkerApplication,
  presentation = "modal",
}) => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [, applicationActions] = useApplicationStore();
  const applicationActionsRef = useRef(applicationActions);
  applicationActionsRef.current = applicationActions;
  const [companyState] = useCompanyStore();
  const [application, setApplication] = useState<ApplicationDto>(applicant);
  const [workerProfileImage, setWorkerProfileImage] = useState<string | null>(
    null,
  );
  const [workerVideo, setWorkerVideo] = useState<string | null>(null);
  const [response, setResponse] = useState<ApplicationResponse | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeApplication, setActiveApplication] = useState<number | null>(
    null,
  );
  const [selectedSkill, setSelectedSkill] =
    useState<ApplicationUserSkillDto | null>(null);
  const [workerEndorsements, setWorkerEndorsements] = useState<
    ApplicationWorkerEndorsementDto[]
  >([]);
  const [skillAlreadyEndorsed, setSkillAlreadyEndorsed] = useState(false);

  const workerName = `${application.worker.firstName} ${application.worker.lastName}`;
  const isPanelPresentation = presentation === "panel";
  const isPublicShift = application.gig.type === GigType.PUBLIC;
  const showContract = shouldShowApplicationContract(application.gig.type);
  const dateOfBirth = application.worker.dateOfBirth?.trim()
    ? formatWorkerDateOfBirth(application.worker.dateOfBirth)
    : null;
  const hoursWorked = getWorkerStat({
    name: "AmountOfHoursWorked",
    stats: application.worker.stats,
  });
  const rating = getWorkerStat({
    name: "Rating",
    stats: application.worker.stats,
  });
  const ratingCount = getWorkerStat({
    name: "AmountOfRatings",
    stats: application.worker.stats,
  });

  useEffect(() => {
    setApplication(applicant);
  }, [applicant]);

  useEffect(() => {
    let isCurrent = true;
    const workerImageId =
      application.worker.externalId?.trim() ||
      application.worker.firebaseId?.trim() ||
      null;
    const workerVideoId = application.worker.firebaseId?.trim() || null;
    if (!workerImageId) {
      setWorkerProfileImage(null);
      setWorkerVideo(null);
      return;
    }

    getWorkerImg(workerImageId)
      .then((image) => {
        if (isCurrent) setWorkerProfileImage(image || null);
      })
      .catch(() => {
        if (isCurrent) setWorkerProfileImage(null);
      });
    if (workerVideoId) {
      getWorkerVideo(workerVideoId)
        .then((video) => {
          if (isCurrent) setWorkerVideo(video);
        })
        .catch(() => {
          if (isCurrent) setWorkerVideo(null);
        });
    } else {
      setWorkerVideo(null);
    }

    return () => {
      isCurrent = false;
    };
  }, [application.worker.externalId, application.worker.firebaseId]);

  useEffect(() => {
    const companyId = companyState.company?.id;
    if (!companyId || !application.worker.id) return;
    let isCurrent = true;
    applicationActionsRef.current
      .getApplicationWorkerEndorsements(companyId, application.worker.id)
      .then((result) => {
        if (isCurrent) setWorkerEndorsements(result.data);
      })
      .catch(() => {
        if (isCurrent) setWorkerEndorsements([]);
      });
    return () => {
      isCurrent = false;
    };
  }, [application.worker.id, companyState.company?.id]);

  const closeApplicationModal = (): void => {
    setWorkerApplication?.(null);
    setShowApplicationModal(false);
  };

  const updateParentApplications = (updated: ApplicationDto): void => {
    onApplicationUpdated?.(updated);
    if (setApplications) {
      setApplications((current) =>
        unhandled
          ? current.filter((item) => item.id !== updated.id)
          : current.map((item) => (item.id === updated.id ? updated : item)),
      );
    }
  };

  const showUpdateError = (): void => {
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: t("Alerts.OhNo"),
        message: t("ApplicationsCompany.ApplicationCouldNotBeUpdated"),
      },
    });
  };

  const updateApplicationStatus = async (
    requestedStatus: ApplicationStatus.OFFERED | ApplicationStatus.CLOSED,
  ): Promise<void> => {
    const companyId = companyState.company?.id;
    if (!companyId) return;
    const statusUpdate = toApplicationStatusUpdate(requestedStatus);
    const updated: ApplicationDto = {
      ...application,
      ...statusUpdate,
      ...(statusUpdate.resolution === ApplicationResolution.REJECTED && {
        declineReason,
      }),
    };
    setIsSubmitting(true);
    try {
      const response = await applicationActions.updateApplicationStatus(
        companyId,
        application.id,
        updated,
      );
      const savedApplication = response?.data ?? updated;
      setApplication(savedApplication);
      updateParentApplications(savedApplication);
      setResponse(null);
      setDeclineReason("");
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t(
            requestedStatus === ApplicationStatus.OFFERED
              ? "ApplicationsCompany.Assigned"
              : "ApplicationsCompany.Rejected",
          ),
          message: t(
            requestedStatus === ApplicationStatus.OFFERED
              ? "ApplicationsCompany.ApplicantHasBeenAssignedGig"
              : "ApplicationsCompany.ApplicationHasBeenRejected",
          ),
        },
      });
    } catch {
      showUpdateError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const findCompanyEndorsement = (
    skill: string,
  ): ApplicationWorkerEndorsementDto | undefined => {
    return workerEndorsements.find(
      (endorsement) =>
        endorsement.companyId === companyState.company?.id &&
        endorsement.skill.toLowerCase() === skill.toLowerCase(),
    );
  };

  const openSkill = (skill: ApplicationUserSkillDto): void => {
    setSkillAlreadyEndorsed(Boolean(findCompanyEndorsement(skill.skill)));
    setSelectedSkill(skill);
  };

  const createEndorsement = async (skill: string): Promise<void> => {
    const companyId = companyState.company?.id;
    if (!companyId || !application.worker.id || findCompanyEndorsement(skill))
      return;
    try {
      const result =
        await applicationActions.createApplicationWorkerEndorsement(
          companyId,
          application.worker.id,
          skill,
        );
      if (!result.data?.id) return;
      setWorkerEndorsements((current) => [...current, result.data]);
      setSelectedSkill(null);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t("ApplicationsCompany.Endorsed"),
          message: t("ApplicationsCompany.CreateEndorsementSuccessfully"),
        },
      });
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("Alerts.SomethingWentWrong"),
        },
      });
    }
  };

  const deleteEndorsement = async (skill: string): Promise<void> => {
    const endorsement = findCompanyEndorsement(skill);
    if (!endorsement) {
      setSelectedSkill(null);
      return;
    }
    try {
      const result =
        await applicationActions.deleteApplicationWorkerEndorsement(
          endorsement.companyId,
          endorsement.workerId,
          endorsement.id,
        );
      if (result.status !== 200) return;
      setWorkerEndorsements((current) =>
        current.filter((item) => item.id !== endorsement.id),
      );
      setSelectedSkill(null);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t("General.Deleted"),
          message: t("ApplicationsCompany.DeleteEndorsementSuccessfully"),
        },
      });
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("Alerts.SomethingWentWrong"),
        },
      });
    }
  };

  const statusChip = toApplicationStatusChip(application);
  const responseTitle =
    response === "accept"
      ? t("ApplicationsCompany.AssignApplicant", { name: workerName })
      : t("GigsCompany.YouAreAboutToRejectApplication", {
          user: application.worker.firstName,
        });
  const displayPhoneNumber = fromApiPhoneNumber(application.worker.phoneNumber);
  const contactDetails = (
    <ContactList>
      {application.worker.email && (
        <ContactLink href={`mailto:${application.worker.email}`}>
          {application.worker.email}
        </ContactLink>
      )}
      {displayPhoneNumber && (
        <ContactLink href={`tel:${displayPhoneNumber}`}>
          {displayPhoneNumber}
        </ContactLink>
      )}
    </ContactList>
  );
  const displayValue = (value: string | null | undefined): string =>
    value?.trim() || t("General.None");
  const personalInfoItems: Array<{ label: string; value: string }> = [
    {
      label: t("ProfileWorker.PhoneNumber"),
      value: displayValue(displayPhoneNumber),
    },
    {
      label: t("ProfileWorker.DateOfBirth"),
      value: displayValue(dateOfBirth),
    },
    {
      label: t("ProfileWorker.Email"),
      value: displayValue(application.worker.email),
    },
  ];
  if (application.worker.address?.trim()) {
    personalInfoItems.push({
      label: t("ProfileWorker.Address"),
      value: application.worker.address.trim(),
    });
  }
  if (application.worker.postalCode?.trim()) {
    personalInfoItems.push({
      label: t("ProfileWorker.PostalCode"),
      value: application.worker.postalCode.trim(),
    });
  }
  if (application.worker.city?.trim()) {
    personalInfoItems.push({
      label: t("ProfileWorker.City"),
      value: application.worker.city.trim(),
    });
  }
  const motivationalLetterContent = (
    <Text typography="b2" color={ColorV2.Blue2}>
      {application.motivationalLetter || t("General.None")}
    </Text>
  );
  const descriptionContent = (
    <Text typography="b2" color={ColorV2.Blue2}>
      {application.worker.description || t("General.None")}
    </Text>
  );
  const videoContent = workerVideo ? (
    <ApplicantVideoWrapper>
      <ApplicantVideo
        aria-label={t("ProfileWorker.VideoPresentation")}
        controls
        src={workerVideo}
      />
    </ApplicantVideoWrapper>
  ) : (
    <Text typography="b2" color={ColorV2.Blue4}>
      {t("ProfileWorker.NoVideoPresentation")}
    </Text>
  );
  const skillsContent = (
    <SkillsList>
      {application.worker.skills.map((skill) => {
        const endorsementCount = workerEndorsements.filter(
          (endorsement) =>
            endorsement.skill.toLowerCase() === skill.skill.toLowerCase(),
        ).length;
        return (
          <SkillItem key={`${skill.skill}-${skill.experience}`}>
            <SkillCopy>
              <Text typography="b2">{t(skill.skill, { ns: "skills" })}</Text>
              <Text typography="s1" color={ColorV2.Blue4}>
                {toExperienceLabel({
                  experience: skill.experience,
                  monthsLabel: t("ProfileWorker.Months"),
                  yearsLabel: t("ProfileWorker.Years"),
                })}
              </Text>
            </SkillCopy>
            <SecondaryButton
              size="small"
              label={`${endorsementCount} ${t("ProfileWorker.Endorsements")}`}
              onClick={() => openSkill(skill)}
            />
          </SkillItem>
        );
      })}
    </SkillsList>
  );
  const documentsContent = (
    <WorkerDocuments
      documents={application.worker.documents}
      presentation="design-v2-card-list"
    />
  );

  return (
    <>
      <ApplicantDetailsShell
        presentation={presentation}
        isOpen={showApplicationModal}
        onClose={closeApplicationModal}
        title={t("ApplicationsCompany.ApplicationDetails")}
        footer={
          <FooterActions>
            {showContract && (
              <SecondaryButton
                label={t("GigsWorker.Contract")}
                iconName="v2/document"
                onClick={() => setActiveApplication(application.id)}
              />
            )}
            {statusChip && (
              <ApplicantStatusBadge
                $backgroundColor={statusChip.backgroundColor}
                $textColor={statusChip.textColor}
              >
                {t(statusChip.labelKey)}
              </ApplicantStatusBadge>
            )}
            {application.status === ApplicationStatus.PENDING && (
              <>
                <SecondaryButton
                  label={t("GigsCompany.Reject")}
                  onClick={() => {
                    setDeclineReason("");
                    setResponse("reject");
                  }}
                  styleOverrides={{
                    textColor: ColorV2.NegativeRed,
                    borderColor: ColorV2.NegativeRed,
                    iconColor: ColorV2.NegativeRed,
                  }}
                />
                <PrimaryButton
                  label={t("ApplicationsCompany.Assign")}
                  styleOverrides={ASSIGN_BUTTON_STYLES}
                  onClick={() => setResponse("accept")}
                />
              </>
            )}
          </FooterActions>
        }
      >
        <ApplicantContentGrid $panel={isPanelPresentation}>
          <ApplicantProfileColumn $panel={isPanelPresentation}>
            <ApplicantProfilePanel $panel={isPanelPresentation}>
              <ApplicantProfileAvatar $panel={isPanelPresentation}>
                {workerProfileImage ? (
                  <ApplicantProfileImage
                    src={workerProfileImage}
                    alt={workerName}
                  />
                ) : (
                  <CustomIcon
                    name="v2/person"
                    size="56px"
                    color={ColorV2.Blue4}
                  />
                )}
              </ApplicantProfileAvatar>
              <ApplicantProfileIdentity $panel={isPanelPresentation}>
                <ApplicantProfileName $panel={isPanelPresentation}>
                  {workerName}
                </ApplicantProfileName>
                {isPanelPresentation ? (
                  <ApplicantProfileTags>
                    {hoursWorked !== null && (
                      <ApplicantProfileTag>
                        {hoursWorked.toFixed(0)}{" "}
                        {t("ProfileWorker.HoursWorked")}
                      </ApplicantProfileTag>
                    )}
                    <ApplicantProfileTag>
                      {rating === null
                        ? t("ProfileWorker.NoRatingsYet")
                        : `${rating.toFixed(1)}/5 (${ratingCount?.toFixed(0) ?? 0}) ${t("ProfileWorker.Rating")}`}
                    </ApplicantProfileTag>
                  </ApplicantProfileTags>
                ) : (
                  application.worker.dateOfBirth?.trim() && (
                    <ProfileMeta>
                      {formatWorkerDateOfBirth(application.worker.dateOfBirth)}
                    </ProfileMeta>
                  )
                )}
              </ApplicantProfileIdentity>
              {!isPanelPresentation && (
                <ApplicantSection>
                  <Text typography="b1">{t("ProfileWorker.Contact")}</Text>
                  {contactDetails}
                </ApplicantSection>
              )}
            </ApplicantProfilePanel>
            {!isPanelPresentation && (
              <StatGrid>
                {hoursWorked !== null && (
                  <StatCard>
                    <StatValue>
                      <CustomIcon
                        name="v2/clock"
                        size="20px"
                        color={ColorV2.Blue2}
                      />
                      {hoursWorked.toFixed(0)}
                    </StatValue>
                    <Text typography="s1" color={ColorV2.Blue4}>
                      {t("ProfileWorker.HoursWorked")}
                    </Text>
                  </StatCard>
                )}
                <StatCard>
                  <StatValue>
                    <CustomIcon
                      name="v2/star"
                      size="20px"
                      color={ColorV2.Blue2}
                    />
                    {rating === null ? "-" : `${rating.toFixed(1)}/5`}
                    {rating !== null && (
                      <Text typography="s1" color={ColorV2.Blue4}>
                        ({ratingCount?.toFixed(0) ?? 0})
                      </Text>
                    )}
                  </StatValue>
                  <Text typography="s1" color={ColorV2.Blue4}>
                    {rating === null
                      ? t("ProfileWorker.NoRatingsYet")
                      : t("ProfileWorker.Rating")}
                  </Text>
                </StatCard>
              </StatGrid>
            )}
          </ApplicantProfileColumn>

          <ApplicantSection>
            {isPanelPresentation ? (
              <>
                <ApplicantPanelSection>
                  <ApplicantPanelHeading>
                    {t("ProfileWorker.PersonalInformation")}
                  </ApplicantPanelHeading>
                  <ApplicantPanelCard>
                    <ApplicantInfoGrid>
                      {personalInfoItems.map(({ label, value }) => (
                        <ApplicantInfoItem key={label}>
                          <Text typography="b1">{value}</Text>
                          <Text typography="s1" color={ColorV2.Blue4}>
                            {label}
                          </Text>
                        </ApplicantInfoItem>
                      ))}
                    </ApplicantInfoGrid>
                  </ApplicantPanelCard>
                </ApplicantPanelSection>

                <ApplicantPanelSection>
                  <ApplicantPanelHeading>
                    {t("ProfileWorker.Description")}
                  </ApplicantPanelHeading>
                  <ApplicantPanelCard>
                    <ApplicantPanelText>
                      {descriptionContent}
                    </ApplicantPanelText>
                  </ApplicantPanelCard>
                </ApplicantPanelSection>

                <ApplicantPanelSection>
                  <ApplicantPanelHeading>
                    {t("ApplicationsCompany.ApplicationDetails")}
                  </ApplicantPanelHeading>
                  <ApplicantPanelCard>
                    <ApplicantInfoGrid>
                      {isPublicShift && (
                        <ApplicantInfoItem>
                          <Text typography="b1">
                            {application.requestedRate} {t("General.Currency")}
                          </Text>
                          <Text typography="s1" color={ColorV2.Blue4}>
                            {t("ProfileWorker.RequestedRate")}
                          </Text>
                        </ApplicantInfoItem>
                      )}
                      <ApplicantInfoItem>
                        <Text typography="b1">{application.gig.role}</Text>
                        <Text typography="s1" color={ColorV2.Blue4}>
                          {t("General.Assignment")}
                        </Text>
                      </ApplicantInfoItem>
                    </ApplicantInfoGrid>
                  </ApplicantPanelCard>
                </ApplicantPanelSection>

                <ApplicantPanelSection>
                  <ApplicantPanelHeading>
                    {t("ProfileWorker.MotivationalLetter")}
                  </ApplicantPanelHeading>
                  <ApplicantPanelCard>
                    {motivationalLetterContent}
                  </ApplicantPanelCard>
                </ApplicantPanelSection>

                {application.declineReason && (
                  <ApplicantPanelSection>
                    <ApplicantPanelHeading>
                      {t("GigsCompany.NoteToApplicant")}
                    </ApplicantPanelHeading>
                    <ApplicantPanelCard>
                      <ApplicantPanelText>
                        <Text typography="b2" color={ColorV2.Blue2}>
                          {application.declineReason}
                        </Text>
                      </ApplicantPanelText>
                    </ApplicantPanelCard>
                  </ApplicantPanelSection>
                )}

                <ApplicantPanelSection>
                  <ApplicantPanelHeading>
                    {t("ProfileWorker.VideoPresentation")}
                  </ApplicantPanelHeading>
                  <ApplicantPanelCard>{videoContent}</ApplicantPanelCard>
                </ApplicantPanelSection>

                <ApplicantPanelSection>
                  <ApplicantPanelHeading>
                    {t("ProfileWorker.Skills")}
                  </ApplicantPanelHeading>
                  <ApplicantPanelCard>{skillsContent}</ApplicantPanelCard>
                </ApplicantPanelSection>

                <ApplicantPanelSection>
                  <ApplicantPanelHeading>
                    {t("General.Documents")}
                  </ApplicantPanelHeading>
                  <ApplicantPanelCard>{documentsContent}</ApplicantPanelCard>
                </ApplicantPanelSection>
              </>
            ) : (
              <>
                <ApplicantSummaryGrid>
                  {isPublicShift && (
                    <ApplicantSummaryItem>
                      <Text typography="s1" color={ColorV2.Blue4}>
                        {t("ProfileWorker.RequestedRate")}
                      </Text>
                      <Text typography="b1">
                        {application.requestedRate} {t("General.Currency")}
                      </Text>
                    </ApplicantSummaryItem>
                  )}
                  <ApplicantSummaryItem>
                    <Text typography="s1" color={ColorV2.Blue4}>
                      {t("General.Assignment")}
                    </Text>
                    <Text typography="b1">{application.gig.role}</Text>
                  </ApplicantSummaryItem>
                </ApplicantSummaryGrid>

                <CollapsibleSection
                  label={t("ProfileWorker.MotivationalLetter")}
                  defaultOpen={Boolean(application.motivationalLetter?.trim())}
                >
                  {motivationalLetterContent}
                </CollapsibleSection>

                {application.declineReason && (
                  <CollapsibleSection
                    label={t("GigsCompany.NoteToApplicant")}
                    defaultOpen
                  >
                    <Text typography="b2" color={ColorV2.Blue2}>
                      {application.declineReason}
                    </Text>
                  </CollapsibleSection>
                )}

                <CollapsibleSection
                  label={t("ProfileWorker.Bio")}
                  defaultOpen={Boolean(application.worker.description?.trim())}
                >
                  {descriptionContent}
                </CollapsibleSection>

                <CollapsibleSection
                  key={`video-${application.id}-${Boolean(workerVideo)}`}
                  label={t("ProfileWorker.VideoPresentation")}
                  defaultOpen={Boolean(workerVideo)}
                >
                  {videoContent}
                </CollapsibleSection>

                <CollapsibleSection
                  label={t("ProfileWorker.Skills")}
                  defaultOpen={application.worker.skills.length > 0}
                >
                  {skillsContent}
                </CollapsibleSection>

                <CollapsibleSection
                  label={t("General.Documents")}
                  defaultOpen={application.worker.documents.length > 0}
                >
                  {documentsContent}
                </CollapsibleSection>
              </>
            )}
          </ApplicantSection>
        </ApplicantContentGrid>
      </ApplicantDetailsShell>

      <Modal
        label={responseTitle}
        size="compact"
        showCloseButton={false}
        isOpen={response !== null}
        onClose={() => {
          setResponse(null);
          setDeclineReason("");
        }}
        footer={
          <FooterActions>
            <SecondaryButton
              label={t("GigsCompany.Discard")}
              onClick={() => {
                setResponse(null);
                setDeclineReason("");
              }}
              disabled={isSubmitting}
            />
            <PrimaryButton
              label={
                response === "accept"
                  ? t("ApplicationsCompany.Assign")
                  : t("GigsCompany.Reject")
              }
              isLoading={isSubmitting}
              styleOverrides={
                response === "reject"
                  ? DESTRUCTIVE_BUTTON_STYLES
                  : ASSIGN_BUTTON_STYLES
              }
              onClick={() => {
                void updateApplicationStatus(
                  response === "accept"
                    ? ApplicationStatus.OFFERED
                    : ApplicationStatus.CLOSED,
                );
              }}
            />
          </FooterActions>
        }
      >
        {response === "reject" ? (
          <TextArea
            label={t("GigsCompany.NoteToApplicant")}
            aria-label={t("GigsCompany.NoteToApplicant")}
            placeholder={t("GigsCompany.SendANote")}
            value={declineReason}
            onChange={setDeclineReason}
            disabled={isSubmitting}
          />
        ) : (
          <Text typography="b2" color={ColorV2.Blue4}>
            {t("ApplicationsCompany.AssignApplicantDescription", {
              name: workerName,
            })}
          </Text>
        )}
      </Modal>

      <Modal
        label={t("ApplicationsCompany.Endorsements")}
        size="compact"
        isOpen={selectedSkill !== null}
        onClose={() => setSelectedSkill(null)}
        footer={
          <FooterActions>
            <SecondaryButton
              label={t("General.Close")}
              onClick={() => setSelectedSkill(null)}
            />
            {selectedSkill && (
              <PrimaryButton
                label={t(
                  skillAlreadyEndorsed
                    ? "ApplicationsCompany.DeleteEndorsement"
                    : "ApplicationsCompany.CreateEndorsement",
                )}
                styleOverrides={
                  skillAlreadyEndorsed ? DESTRUCTIVE_BUTTON_STYLES : undefined
                }
                onClick={() =>
                  skillAlreadyEndorsed
                    ? deleteEndorsement(selectedSkill.skill)
                    : createEndorsement(selectedSkill.skill)
                }
              />
            )}
          </FooterActions>
        }
      >
        {selectedSkill && (
          <ApplicantSection>
            <Text typography="h5">
              {t(selectedSkill.skill, { ns: "skills" })}
            </Text>
            <Text typography="s1" color={ColorV2.Blue4}>
              {toExperienceLabel({
                experience: selectedSkill.experience,
                monthsLabel: t("ProfileWorker.Months"),
                yearsLabel: t("ProfileWorker.Years"),
              })}
            </Text>
            <EndorsementList>
              {workerEndorsements
                .filter(
                  (endorsement) =>
                    endorsement.skill.toLowerCase() ===
                    selectedSkill.skill.toLowerCase(),
                )
                .map((endorsement) => (
                  <Text key={endorsement.id} typography="b2">
                    {endorsement.endorsedBy}
                  </Text>
                ))}
            </EndorsementList>
          </ApplicantSection>
        )}
      </Modal>

      <Modal
        label={t("GigsWorker.Contract")}
        size="mediumSlim"
        isOpen={activeApplication !== null}
        onClose={() => setActiveApplication(null)}
      >
        <GigContract
          applicationId={activeApplication}
          closeContract={() => setActiveApplication(null)}
          showCloseAction={false}
        />
      </Modal>
    </>
  );
};
